import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import { isTexasZipCode } from '../utils/texasZipCode';
import './HomePage.css';

const PRODUCTS = [
  { type: 'home', label: 'Home' },
  { type: 'car', label: 'Auto' },
  { type: 'pet', label: 'Pet' },
  { type: 'renters', label: 'Renters' },
] as const;

type ProductType = (typeof PRODUCTS)[number]['type'];

function HomePage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<ProductType>('home');
  const [zipCode, setZipCode] = useState('');
  const [error, setError] = useState('');
  const [stage, setStage] = useState<'quote' | 'waitlist'>('quote');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);

  const handleStartQuote = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (zipCode.length !== 5 || !/^\d{5}$/.test(zipCode)) {
      setError('Enter a valid 5-digit ZIP code.');
      return;
    }

    if (!isTexasZipCode(zipCode)) {
      setStage('waitlist');
      return;
    }

    sessionStorage.setItem('insuranceType', selectedType);
    sessionStorage.setItem('zipCode', zipCode);
    navigate(`/get-quote?type=${selectedType}`);
  };

  const handleWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setWaitlistLoading(true);
    try {
      await client.post('/insurance/waitlist', {
        email: waitlistEmail.trim(),
        insuranceType: selectedType,
        zipCode,
      });
      setWaitlistSuccess(true);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error || 'Failed to join the waitlist. Please try again.');
    } finally {
      setWaitlistLoading(false);
    }
  };

  return (
    <div className="container home-page">
      <section className="hero">
        <h1>Save up to $500 on insurance</h1>
        <p className="hero-subtitle">
          We find and compare insurance quotes - giving you the best coverage for the lowest price
        </p>

        <div className="hero-quote-panel">
          {stage === 'quote' ? (
            <form className="hero-quote-form" onSubmit={handleStartQuote}>
              <p className="hero-quote-label">What do you need covered?</p>
              <div className="hero-product-pills" role="radiogroup" aria-label="Insurance products">
                {PRODUCTS.map((product) => {
                  const selected = selectedType === product.type;
                  return (
                    <button
                      key={product.type}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className={`hero-product-pill${selected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedType(product.type)}
                    >
                      {product.label}
                    </button>
                  );
                })}
              </div>

              <label className="hero-zip-label" htmlFor="homeZip">
                ZIP code
              </label>
              <div className="hero-zip-row">
                <input
                  id="homeZip"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{5}"
                  maxLength={5}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  placeholder="e.g. 78701"
                  autoComplete="postal-code"
                  required
                />
                <button type="submit" className="btn btn-primary hero-quote-submit">
                  Get my quote
                </button>
              </div>

              {error && <div className="alert alert-error hero-quote-error">{error}</div>}
              <p className="hero-quote-note">Get your quote in under a minute.</p>
            </form>
          ) : (
            <div className="hero-waitlist">
              <h2>We&apos;re in Texas for now</h2>
              <p>
                Leave your email and we&apos;ll notify you when we expand to {zipCode || 'your area'}.
              </p>
              {waitlistSuccess ? (
                <div className="alert alert-success">Thanks — we&apos;ll be in touch.</div>
              ) : (
                <form onSubmit={handleWaitlist}>
                  <div className="hero-zip-row">
                    <input
                      type="email"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      placeholder="you@email.com"
                      required
                      autoComplete="email"
                    />
                    <button
                      type="submit"
                      className="btn btn-primary hero-quote-submit"
                      disabled={waitlistLoading}
                    >
                      {waitlistLoading ? 'Saving...' : 'Notify me'}
                    </button>
                  </div>
                  {error && <div className="alert alert-error hero-quote-error">{error}</div>}
                </form>
              )}
              <button
                type="button"
                className="btn btn-secondary hero-waitlist-back"
                onClick={() => {
                  setStage('quote');
                  setError('');
                  setWaitlistSuccess(false);
                }}
              >
                Try a different ZIP
              </button>
            </div>
          )}
        </div>
      </section>

      <div className="features grid grid-3">
        <div className="card feature-card">
          <div className="feature-icon">📋</div>
          <h3>Easy Setup</h3>
          <p>Share your info in under a minute.</p>
        </div>

        <div className="card feature-card">
          <div className="feature-icon">🔍</div>
          <h3>We Compare</h3>
          <p>We shop and compare rates from top insurance providers for you.</p>
        </div>

        <div className="card feature-card">
          <div className="feature-icon">💰</div>
          <h3>Best Value</h3>
          <p>We&apos;ll find you the best coverage for the best price.</p>
        </div>
      </div>

      <div className="how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h4>Share your contact info</h4>
            <p>Name, email, and phone</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h4>We Shop</h4>
            <p>We shop and compare rates for you</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h4>We Sign You Up</h4>
            <p>We&apos;ll sign you up for the best, cheapest insurance</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h4>Customer Care</h4>
            <p>We&apos;ll continue to find you the best prices</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
